import { motion } from "framer-motion";
import { Instagram, ArrowUpRight } from "lucide-react";
import { BRAND } from "@/data/products";

const HIGHLIGHTS = [
  {
    img: "https://customer-assets-jt897jd0.emergentagent.net/job_vardaan-divine/artifacts/5siajrr6_IMG_20251220_103703.jpg",
    tag: "Signature",
  },
  {
    img: "https://customer-assets-jt897jd0.emergentagent.net/job_vardaan-divine/artifacts/txba9qtk_product-jpeg-500x500-7.jpg",
    tag: "Statues",
  },
  {
    img: "https://customer-assets-jt897jd0.emergentagent.net/job_vardaan-divine/artifacts/pitfm1ze_IMG-20260821-WA0022.jpg",
    tag: "Inlay",
  },
  {
    img: "https://customer-assets-jt897jd0.emergentagent.net/job_vardaan-divine/artifacts/1tijj8j6_FB_IMG_1677315499365.jpg",
    tag: "Guardians",
  },
];

export default function InstagramSection() {
  return (
    <section
      id="instagram"
      data-testid="instagram-section"
      className="py-24 md:py-32 bg-ink-deep text-cream-100 relative overflow-hidden"
    >
      {/* radial glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(ellipse at 30% 40%, rgba(212, 169, 58, 0.18) 0%, transparent 55%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-[1fr_1.1fr] gap-14 lg:gap-20 items-center">
        {/* Left content */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="text-[11px] tracking-[0.32em] uppercase text-gold-light mb-5 font-medium">
            — Follow the Craft
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            See our
            <br />
            <span className="italic gold-text">latest work</span>
            <br />
            on Instagram.
          </h2>
          <p className="text-cream-100/70 mt-6 md:mt-8 text-base md:text-lg leading-relaxed max-w-lg">
            Every temple, statue and sculpture that leaves our karkhana is
            photographed and shared on Instagram. Follow us for fresh work,
            behind-the-scenes videos and stories from the marble craftsmen of
            Makrana.
          </p>

          <a
            href={BRAND.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-cta-btn"
            className="group inline-flex items-center gap-4 mt-8 md:mt-10 px-8 py-5 bg-gold text-ink hover:bg-gold-light transition-colors duration-300"
          >
            <Instagram className="w-6 h-6" />
            <div className="flex flex-col items-start leading-none">
              <span className="text-[10px] tracking-[0.32em] uppercase font-medium mb-1.5">
                Follow us on Instagram
              </span>
              <span className="font-serif text-2xl">@vardaan.arts</span>
            </div>
            <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </a>

          <div className="grid grid-cols-3 gap-4 mt-10 max-w-md">
            <Stat number="13" label="Categories" />
            <Stat number="1000+" label="Temples Fitted" />
            <Stat number="100+" label="Countries" />
          </div>
        </motion.div>

        {/* Right — mosaic grid */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="grid grid-cols-2 gap-4 md:gap-5">
            {HIGHLIGHTS.map((h, i) => (
              <a
                key={i}
                href={BRAND.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className={`group relative overflow-hidden border border-gold/25 hover:border-gold transition-colors ${
                  i === 0
                    ? "aspect-square translate-y-4"
                    : i === 1
                    ? "aspect-square -translate-y-4"
                    : i === 2
                    ? "aspect-square translate-y-4"
                    : "aspect-square -translate-y-4"
                }`}
              >
                <img
                  src={h.img}
                  alt="Instagram highlight"
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-ink-deep/0 group-hover:bg-ink-deep/40 transition-colors duration-500 flex items-center justify-center">
                  <Instagram className="w-8 h-8 text-cream-100 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="absolute top-3 left-3 text-[10px] tracking-[0.24em] uppercase text-gold-light bg-ink-deep/60 px-2 py-1">
                  {h.tag}
                </div>
              </a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ number, label }) {
  return (
    <div className="border-l border-gold/40 pl-3">
      <div className="font-serif text-2xl md:text-3xl text-cream-100">
        {number}
      </div>
      <div className="text-[10px] tracking-[0.24em] uppercase text-gold-light mt-1">
        {label}
      </div>
    </div>
  );
}
